import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Target,
  BookOpen,
  Trophy,
  Users,
  Briefcase,
  GraduationCap,
  CheckCircle,
  Clock,
  Star,
  TrendingUp,
  Lightbulb,
  ArrowRight,
  Plus,
  Edit3
} from 'lucide-react';
import { Card } from '../shared/Card';
import { Button } from '../shared/Button';
import { Badge } from '../shared/Badge';
import { ProgressBar } from '../shared/ProgressBar';

interface Milestone {
  id: string;
  title: string;
  description: string;
  category: 'academic' | 'extracurricular' | 'standardized-tests' | 'college-prep' | 'leadership' | 'research';
  priority: 'high' | 'medium' | 'low';
  timeline: string;
  completed: boolean;
  aiSuggestion?: boolean;
}

interface YearPlan {
  year: string;
  grade: string;
  theme: string;
  description: string;
  keyFocus: string[];
  milestones: Milestone[];
  gpaTarget: number;
  completionRate: number;
}

const roadmapData: YearPlan[] = [
  {
    year: '2024-2025',
    grade: '8th Grade',
    theme: 'Foundation Building',
    description: 'Establish strong academic habits and explore diverse interests',
    keyFocus: ['Study Skills Development', 'Interest Exploration', 'Leadership Opportunities'],
    gpaTarget: 3.7,
    completionRate: 65,
    milestones: [
      {
        id: '8-1',
        title: 'Maintain 3.7+ GPA',
        description: 'Focus on core subjects and develop effective study strategies',
        category: 'academic',
        priority: 'high',
        timeline: 'Ongoing',
        completed: true
      },
      {
        id: '8-2',
        title: 'Join 2-3 Extracurricular Activities',
        description: 'Explore different interests to discover passions',
        category: 'extracurricular',
        priority: 'high',
        timeline: 'Fall 2024',
        completed: true
      },
      {
        id: '8-3',
        title: 'Complete 20 Hours of Community Service',
        description: 'Begin building a service portfolio',
        category: 'extracurricular',
        priority: 'medium',
        timeline: 'Spring 2025',
        completed: false,
        aiSuggestion: true
      },
      {
        id: '8-4',
        title: 'Take PSAT 8/9',
        description: 'Baseline assessment for standardized test preparation',
        category: 'standardized-tests',
        priority: 'medium',
        timeline: 'Spring 2025',
        completed: false
      },
      {
        id: '8-5',
        title: 'Start Leadership Role',
        description: 'Take on a leadership position in chosen activities',
        category: 'leadership',
        priority: 'medium',
        timeline: 'Winter 2025',
        completed: false,
        aiSuggestion: true
      }
    ]
  },
  {
    year: '2025-2026',
    grade: '9th Grade',
    theme: 'Academic Excellence',
    description: 'Establish rigorous academic foundation and deepen extracurricular commitments',
    keyFocus: ['Honors/AP Prep', 'Activity Depth', 'Skill Development'],
    gpaTarget: 3.8,
    completionRate: 0,
    milestones: [
      {
        id: '9-1',
        title: 'Take 2-3 Honors Courses',
        description: 'Challenge yourself academically with advanced coursework',
        category: 'academic',
        priority: 'high',
        timeline: 'Fall 2025',
        completed: false
      },
      {
        id: '9-2',
        title: 'Maintain Leadership Positions',
        description: 'Continue and expand leadership roles in activities',
        category: 'leadership',
        priority: 'high',
        timeline: 'Ongoing',
        completed: false
      },
      {
        id: '9-3',
        title: 'Complete Summer Program',
        description: 'Attend academic or skill-building summer program',
        category: 'college-prep',
        priority: 'medium',
        timeline: 'Summer 2026',
        completed: false,
        aiSuggestion: true
      },
      {
        id: '9-4',
        title: 'Start Research Project',
        description: 'Begin independent research in area of interest',
        category: 'research',
        priority: 'medium',
        timeline: 'Spring 2026',
        completed: false,
        aiSuggestion: true
      },
      {
        id: '9-5',
        title: 'Take PSAT/NMSQT',
        description: 'Practice test for National Merit consideration',
        category: 'standardized-tests',
        priority: 'medium',
        timeline: 'Fall 2025',
        completed: false
      }
    ]
  },
  {
    year: '2026-2027',
    grade: '10th Grade',
    theme: 'Specialization & Recognition',
    description: 'Develop expertise in chosen areas and begin building recognition',
    keyFocus: ['AP Coursework', 'Awards & Recognition', 'Research Excellence'],
    gpaTarget: 3.9,
    completionRate: 0,
    milestones: [
      {
        id: '10-1',
        title: 'Take 3-4 AP Courses',
        description: 'Demonstrate readiness for college-level work',
        category: 'academic',
        priority: 'high',
        timeline: 'Fall 2026',
        completed: false
      },
      {
        id: '10-2',
        title: 'Win Regional/State Award',
        description: 'Achieve recognition for excellence in chosen field',
        category: 'extracurricular',
        priority: 'high',
        timeline: 'Spring 2027',
        completed: false,
        aiSuggestion: true
      },
      {
        id: '10-3',
        title: 'Complete Significant Research',
        description: 'Present research at science fair or academic conference',
        category: 'research',
        priority: 'high',
        timeline: 'Spring 2027',
        completed: false
      },
      {
        id: '10-4',
        title: 'Begin College Visits',
        description: 'Start exploring potential colleges and programs',
        category: 'college-prep',
        priority: 'medium',
        timeline: 'Spring 2027',
        completed: false
      },
      {
        id: '10-5',
        title: 'SAT/ACT Prep Course',
        description: 'Begin formal preparation for standardized tests',
        category: 'standardized-tests',
        priority: 'medium',
        timeline: 'Summer 2027',
        completed: false
      }
    ]
  },
  {
    year: '2027-2028',
    grade: '11th Grade',
    theme: 'College Application Preparation',
    description: 'Focus on standardized testing and college application building',
    keyFocus: ['Standardized Testing', 'College List Building', 'Essay Writing'],
    gpaTarget: 3.95,
    completionRate: 0,
    milestones: [
      {
        id: '11-1',
        title: 'Take SAT/ACT (Target: 1500+/34+)',
        description: 'Achieve competitive standardized test scores',
        category: 'standardized-tests',
        priority: 'high',
        timeline: 'Spring 2028',
        completed: false
      },
      {
        id: '11-2',
        title: 'Build College List (15-20 schools)',
        description: 'Research and create balanced list of target colleges',
        category: 'college-prep',
        priority: 'high',
        timeline: 'Summer 2028',
        completed: false
      },
      {
        id: '11-3',
        title: 'Draft College Essays',
        description: 'Begin writing personal statement and supplemental essays',
        category: 'college-prep',
        priority: 'high',
        timeline: 'Summer 2028',
        completed: false
      },
      {
        id: '11-4',
        title: 'National Recognition/Award',
        description: 'Achieve national-level recognition in chosen field',
        category: 'extracurricular',
        priority: 'medium',
        timeline: 'Spring 2028',
        completed: false,
        aiSuggestion: true
      },
      {
        id: '11-5',
        title: 'College Interviews Practice',
        description: 'Prepare for admission interviews with mock sessions',
        category: 'college-prep',
        priority: 'medium',
        timeline: 'Fall 2027',
        completed: false
      }
    ]
  },
  {
    year: '2028-2029',
    grade: '12th Grade',
    theme: 'Application Excellence',
    description: 'Execute flawless college application strategy and maintain excellence',
    keyFocus: ['Application Submission', 'Scholarship Applications', 'Final Achievements'],
    gpaTarget: 4.0,
    completionRate: 0,
    milestones: [
      {
        id: '12-1',
        title: 'Submit All College Applications',
        description: 'Complete and submit all regular and early applications',
        category: 'college-prep',
        priority: 'high',
        timeline: 'December 2028',
        completed: false
      },
      {
        id: '12-2',
        title: 'Apply for 10+ Scholarships',
        description: 'Pursue financial aid through scholarship applications',
        category: 'college-prep',
        priority: 'high',
        timeline: 'Spring 2029',
        completed: false
      },
      {
        id: '12-3',
        title: 'Maintain Academic Excellence',
        description: 'Avoid senioritis and maintain strong GPA',
        category: 'academic',
        priority: 'high',
        timeline: 'Ongoing',
        completed: false
      },
      {
        id: '12-4',
        title: 'Complete Capstone Project',
        description: 'Finish significant independent project or research',
        category: 'research',
        priority: 'medium',
        timeline: 'Spring 2029',
        completed: false
      },
      {
        id: '12-5',
        title: 'College Decision & Enrollment',
        description: 'Make final college choice and complete enrollment',
        category: 'college-prep',
        priority: 'high',
        timeline: 'May 2029',
        completed: false
      }
    ]
  }
];

const categoryIcons = {
  academic: BookOpen,
  extracurricular: Users,
  'standardized-tests': Target,
  'college-prep': GraduationCap,
  leadership: Trophy,
  research: Lightbulb
};

const categoryColors = {
  academic: 'primary',
  extracurricular: 'secondary',
  'standardized-tests': 'accent',
  'college-prep': 'primary',
  leadership: 'secondary',
  research: 'accent'
} as const;

export const StrategicRoadmap: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState<string>('2024-2025');
  const [showAISuggestions, setShowAISuggestions] = useState(true);

  const selectedPlan = roadmapData.find(plan => plan.year === selectedYear);

  const toggleMilestone = (milestoneId: string) => {
    // In a real app, this would update the backend
    console.log('Toggling milestone:', milestoneId);
  };

  const addCustomMilestone = () => {
    // In a real app, this would open a form modal
    console.log('Adding custom milestone');
  };

  const overallProgress = roadmapData.reduce((acc, year) => {
    const completed = year.milestones.filter(m => m.completed).length;
    const total = year.milestones.length;
    return acc + (completed / total);
  }, 0) / roadmapData.length * 100;

  return (
    <div className="space-y-8">
      {/* Header */}
      <Card className="bg-gradient-to-br from-primary-500 to-accent-500 text-white">
        <div className="text-center">
          <TrendingUp className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Your Strategic Roadmap</h2>
          <p className="text-primary-100 text-lg mb-6">
            AI-powered multi-year plan to maximize your college preparation journey
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{Math.round(overallProgress)}%</div>
              <div className="text-sm text-primary-100">Overall Progress</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">25</div>
              <div className="text-sm text-primary-100">Total Milestones</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">4.5</div>
              <div className="text-sm text-primary-100">Years to College</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Year Selection */}
      <Card>
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Academic Year Timeline</h3>
        <div className="flex flex-wrap gap-3">
          {roadmapData.map((plan) => (
            <button
              key={plan.year}
              onClick={() => setSelectedYear(plan.year)}
              className={`px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                selectedYear === plan.year
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
              }`}
            >
              <div className="text-sm font-semibold">{plan.grade}</div>
              <div className="text-xs text-gray-600">{plan.year}</div>
              <div className="mt-1">
                <ProgressBar
                  progress={plan.completionRate}
                  size="sm"
                  color="primary"
                  className="w-16"
                />
              </div>
            </button>
          ))}
        </div>
      </Card>

      {/* Selected Year Details */}
      {selectedPlan && (
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedYear}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {/* Year Overview */}
            <Card>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {selectedPlan.grade}: {selectedPlan.theme}
                  </h3>
                  <p className="text-gray-600 mb-4">{selectedPlan.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedPlan.keyFocus.map((focus, index) => (
                      <Badge key={index} variant="primary" size="sm">
                        {focus}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-primary-600 mb-1">
                    {selectedPlan.gpaTarget}
                  </div>
                  <div className="text-sm text-gray-600">Target GPA</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Progress Overview</h4>
                  <ProgressBar
                    progress={selectedPlan.completionRate}
                    color="primary"
                    showPercentage
                    className="mb-2"
                  />
                  <p className="text-sm text-gray-600">
                    {selectedPlan.milestones.filter(m => m.completed).length} of {selectedPlan.milestones.length} milestones completed
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">AI Recommendations</h4>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowAISuggestions(!showAISuggestions)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        showAISuggestions
                          ? 'bg-accent-100 text-accent-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {showAISuggestions ? 'Hide' : 'Show'} AI Suggestions
                    </button>
                    <Badge variant="accent" size="sm">
                      {selectedPlan.milestones.filter(m => m.aiSuggestion).length} AI Tips
                    </Badge>
                  </div>
                </div>
              </div>
            </Card>

            {/* Milestones */}
            <Card>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Year Milestones</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addCustomMilestone}
                  className="flex items-center space-x-2"
                >
                  <Plus size={16} />
                  <span>Add Custom</span>
                </Button>
              </div>

              <div className="space-y-4">
                {selectedPlan.milestones.map((milestone, index) => {
                  const Icon = categoryIcons[milestone.category];
                  const shouldShow = !milestone.aiSuggestion || showAISuggestions;

                  if (!shouldShow) return null;

                  return (
                    <motion.div
                      key={milestone.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`border rounded-xl p-4 transition-all duration-200 ${
                        milestone.completed
                          ? 'border-green-200 bg-green-50'
                          : milestone.aiSuggestion
                          ? 'border-accent-200 bg-accent-50'
                          : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start space-x-4">
                        <button
                          onClick={() => toggleMilestone(milestone.id)}
                          className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                            milestone.completed
                              ? 'border-green-500 bg-green-500 text-white'
                              : 'border-gray-300 hover:border-primary-500'
                          }`}
                        >
                          {milestone.completed && <CheckCircle size={14} />}
                        </button>

                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <Icon size={18} className={`text-${categoryColors[milestone.category]}-600`} />
                            <h4 className={`font-semibold ${
                              milestone.completed ? 'text-green-700 line-through' : 'text-gray-900'
                            }`}>
                              {milestone.title}
                            </h4>
                            <div className="flex items-center space-x-2">
                              <Badge
                                variant={categoryColors[milestone.category]}
                                size="sm"
                              >
                                {milestone.category.replace('-', ' ')}
                              </Badge>
                              {milestone.priority === 'high' && (
                                <Badge variant="warning" size="sm">High Priority</Badge>
                              )}
                              {milestone.aiSuggestion && (
                                <Badge variant="accent" size="sm">
                                  <Star size={12} className="mr-1" />
                                  AI Suggested
                                </Badge>
                              )}
                            </div>
                          </div>

                          <p className={`text-sm mb-2 ${
                            milestone.completed ? 'text-green-600' : 'text-gray-600'
                          }`}>
                            {milestone.description}
                          </p>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                              <Clock size={14} />
                              <span>{milestone.timeline}</span>
                            </div>
                            {!milestone.completed && (
                              <Button variant="ghost" size="sm" className="flex items-center space-x-1">
                                <Edit3 size={14} />
                                <span>Edit</span>
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </Card>

            {/* AI Insights */}
            <Card className="bg-gradient-to-r from-accent-50 to-primary-50">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center">
                  <Lightbulb className="w-6 h-6 text-accent-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    AI Strategic Insights
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Based on your current progress and college goals, here are personalized recommendations for {selectedPlan.grade}:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <ArrowRight size={16} className="text-accent-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">
                        Focus on building depth in 2-3 activities rather than breadth
                      </span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <ArrowRight size={16} className="text-accent-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">
                        Consider starting a passion project that demonstrates leadership
                      </span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <ArrowRight size={16} className="text-accent-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">
                        Begin documenting achievements for future college applications
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};